// app_client/src/components/modals/AccountSelectModal.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Loader2, Plus, Search, User, X, ChevronLeft } from 'lucide-react';
import Pagination from '../common/Pagination';
import { toast } from 'react-hot-toast';
import useAccountStore from '../../store/useAccountStore';
import { getAccountTypeLabel } from '../../utils/accountLabels';
import useAccountCodeWorkflow from '../../hooks/useAccountCodeWorkflow';
import QuickPersonnelModal from './QuickPersonnelModal';
import useAuthStore from '../../store/authStore';

const initialPagination = {
  totalItems: 0,
  currentPage: 1,
  totalPages: 1,
  limit: 10
};

const initialCreateForm = {
  name: '',
  code: '',
  phone: '',
  phone2: '',
  email: '',
  taxOffice: '',
  taxNumber: ''
};

const getSearchPlaceholder = (accountType) => (
  accountType === 'EMPLOYEE'
    ? 'Personel adı, kodu, telefon veya vergi no ile ara...'
    : 'Unvan, isim, telefon veya vergi no ile ara...'
);

const AccountSelectModal = ({
  isOpen,
  onClose,
  onSelect,
  accountType = 'CUSTOMER',
  title,
  enableQuickCreate = true
}) => {
  const { saveAccount, getNextCode, checkCodeUsage } = useAccountStore();
  const [search, setSearch] = useState('');
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState(initialPagination);
  const [viewState, setViewState] = useState('LIST');
  const [createForm, setCreateForm] = useState(initialCreateForm);
  const [createSaving, setCreateSaving] = useState(false);
  const [personnelStatus, setPersonnelStatus] = useState('Active');
  const requestId = useRef(0);
  const [quickPersonnelOpen, setQuickPersonnelOpen] = useState(false);
  const hasPermission = useAuthStore((state) => state.hasPermission);
  const canCreatePersonnel = accountType === 'EMPLOYEE' && enableQuickCreate && hasPermission('account', 'write');

  const canQuickCreate = enableQuickCreate && accountType === 'CUSTOMER';

  const {
    codeChecking: createCodeChecking,
    isCodeAvailable: isCreateCodeAvailable,
    refreshCode: refreshCreateCode
  } = useAccountCodeWorkflow({
    code: createForm.code,
    setCode: (nextCode) => setCreateForm((current) => ({
      ...current,
      code: nextCode
    })),
    type: accountType,
    originalCode: '',
    originalType: '',
    isNew: true,
    enabled: isOpen && viewState === 'CREATE' && canQuickCreate,
    getNextCode,
    checkCodeUsage
  });

  const resetCreateState = useCallback(() => {
    setCreateForm(initialCreateForm);
    setCreateSaving(false);
  }, []);

  const resetLocalState = useCallback(() => {
    setSearch('');
    setPersonnelStatus('Active');
    setQuickPersonnelOpen(false);
    setAccounts([]);
    setLoading(false);
    setError('');
    setPagination(initialPagination);
    setViewState('LIST');
    resetCreateState();
  }, [resetCreateState]);

  useEffect(() => {
    if (viewState !== 'LIST') return undefined;

    resetCreateState();
    return undefined;
  }, [resetCreateState, viewState]);

  const fetchAccounts = useCallback(async (searchValue, page = 1, signal) => {
    const currentRequest = ++requestId.current;
    const params = {
      search: searchValue,
      viewType: 'all',
      type: accountType,
      status: accountType === 'EMPLOYEE' ? personnelStatus : undefined,
      page,
      limit: initialPagination.limit
    };

    Object.keys(params).forEach((key) => {
      if (params[key] === undefined || params[key] === null || params[key] === '') {
        delete params[key];
      }
    });

    setLoading(true);
    setError('');

    try {
      const response = await axios.get('/api/accounts', { params, signal });
      if (currentRequest !== requestId.current || signal?.aborted) return;
      const items = response.data.data || response.data.items || response.data || [];
      const meta = response.data.meta || response.data;

      setAccounts(Array.isArray(items) ? items : []);
      setPagination({
        totalItems: meta.total || meta.totalItems || 0,
        currentPage: meta.page || meta.currentPage || 1,
        totalPages: meta.totalPages || 1,
        limit: meta.limit || initialPagination.limit
      });
    } catch (err) {
      if (currentRequest !== requestId.current) return;
      if (axios.isCancel(err) || err.code === 'ERR_CANCELED') return;
      setAccounts([]);
      setError(err.response?.data?.message || 'Cariler yüklenemedi.');
      console.error('Cari seçim sonuçları yüklenemedi:', err);
    } finally {
      if (currentRequest === requestId.current) setLoading(false);
    }
  }, [accountType, personnelStatus]);

  useEffect(() => {
    if (!isOpen) {
      resetLocalState();
      return undefined;
    }

    const controller = new AbortController();
    const debounceTimer = window.setTimeout(() => {
      fetchAccounts(search.trim(), 1, controller.signal);
    }, 300);

    return () => {
      window.clearTimeout(debounceTimer);
      controller.abort();
    };
  }, [fetchAccounts, isOpen, resetLocalState, search]);

  const handleClose = () => {
    resetLocalState();
    onClose?.();
  };

  const handleSelect = (account) => {
    onSelect?.(account);
    handleClose();
  };

  const openCreateView = () => {
    if (!canQuickCreate) return;
    resetCreateState();
    setViewState('CREATE');
  };

  const handleCreateBack = () => {
    resetCreateState();
    setViewState('LIST');
  };

  const handleCreateFieldChange = (field) => (event) => {
    setCreateForm((current) => ({
      ...current,
      [field]: event.target.value
    }));
  };

  const handleCreateCodeRefresh = async () => {
    await refreshCreateCode();
  };

  const handleCreateSubmit = async (event) => {
    event.preventDefault();
    if (!isCreateCodeAvailable) {
      toast.error('Lütfen geçerli ve benzersiz bir cari kodu girin.');
      return;
    }

    setCreateSaving(true);

    try {

      const result = await saveAccount({
        ...createForm,
        type: 'CUSTOMER'
      });

      if (!result.success) {
        toast.error(result.message || 'Müşteri oluşturulamadı');
        return;
      }

      toast.success('Müşteri oluşturuldu');
      handleSelect(result.data || result.savedData || result);
    } catch (err) {
      console.error('Müşteri hızlı oluşturma başarısız:', err);
      toast.error(err.response?.data?.message || 'Müşteri oluşturulamadı');
    } finally {
      setCreateSaving(false);
    }
  };

  const headerTitle = title || (accountType === 'EMPLOYEE' ? 'Personel Seçimi' : 'Cari Hesap Seçimi');
  const emptyStateLabel = search.trim()
    ? 'Kayıt Bulunamadı'
    : accountType === 'EMPLOYEE'
      ? 'Personel bulunamadı'
      : 'Cari bulunamadı';

  const summaryLabel = useMemo(() => {
    if (!pagination.totalPages || pagination.totalPages <= 1) return '';
    return `${pagination.totalItems} sonuçtan ${accounts.length} kayıt gösteriliyor`;
  }, [accounts.length, pagination.totalItems, pagination.totalPages]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-6 bg-slate-900/40 backdrop-blur-md overflow-y-auto">
      <div className="bg-white w-full max-w-2xl mx-auto max-h-[calc(100vh-2rem)] md:max-h-[calc(100vh-3rem)] rounded-[3rem] shadow-2xl overflow-hidden border border-slate-100 animate-in zoom-in-95">
        <div className="p-6 border-b border-slate-50 flex justify-between items-center bg-slate-50/50">
          <div className="flex items-center gap-3 ml-2 min-w-0">
            <div className="p-2 bg-blue-100 text-blue-600 rounded-xl">
              <User size={20} />
            </div>
            <div className="min-w-0">
              <h3 className="font-black text-slate-800 uppercase tracking-tighter truncate">{headerTitle}</h3>
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                {accountType === 'EMPLOYEE' ? 'Personel carisi' : 'Müşteri / cari seçimi'}
              </p>
            </div>
          </div>
          {accountType === 'EMPLOYEE' && <label className="ml-auto mr-3 text-xs font-bold">Durum<select aria-label="Personel durumu" className="ml-2 rounded-xl border p-2" value={personnelStatus} onChange={(event) => { requestId.current += 1; setAccounts([]); setLoading(true); setPersonnelStatus(event.target.value); }}><option value="Active">Aktif</option><option value="Passive">Pasif</option></select></label>}
          <button type="button" onClick={handleClose} className="p-2 hover:bg-slate-200 rounded-full transition-all">
            <X size={20} />
          </button>
        </div>

        <div className="h-full overflow-hidden">
          <div
            className={`flex h-full w-[200%] transition-transform duration-300 ease-in-out ${
              viewState === 'CREATE' ? '-translate-x-1/2' : 'translate-x-0'
            }`}
          >
            <section className="flex h-full w-1/2 shrink-0 flex-col overflow-hidden">
              <div className="p-6 border-b border-slate-50 space-y-4">
                <div className="relative">
                  <Search className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                  <input
                    autoFocus={viewState === 'LIST'}
                    type="text"
                    placeholder={getSearchPlaceholder(accountType)}
                    className="w-full pl-14 pr-6 py-4 bg-slate-100 rounded-2xl border-none font-bold text-slate-700 focus:ring-2 ring-blue-500/20 outline-none"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                  />
                </div>

                <div className="flex items-center justify-between gap-3">
                  <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                    {accountType ? getAccountTypeLabel(accountType) : 'Cari'}
                  </p>
                  <button
                    type="button"
                    onClick={() => accountType === 'EMPLOYEE' ? setQuickPersonnelOpen(true) : openCreateView()}
                    disabled={accountType === 'EMPLOYEE' ? !canCreatePersonnel : !canQuickCreate}
                    className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-4 py-2 text-[10px] font-black uppercase tracking-widest text-white transition-colors hover:bg-black disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <Plus size={12} />
                    {accountType === 'EMPLOYEE' ? 'Yeni Personel Oluştur' : 'Yeni Müşteri Ekle'}
                  </button>
                </div>
              </div>

              <div className="px-4 pb-4 max-h-[45vh] min-h-[260px] overflow-y-auto space-y-4">
                {loading ? (
                  <div className="flex flex-col items-center justify-center py-20 gap-3">
                    <Loader2 className="animate-spin text-blue-600" />
                    <p className="text-[10px] font-black text-slate-400 uppercase">Cariler Yükleniyor...</p>
                  </div>
                ) : error ? (
                  <div className="py-20 text-center">
                    <p className="text-xs font-bold text-rose-500 uppercase">{error}</p>
                  </div>
                ) : Array.isArray(accounts) && accounts.length > 0 ? (
                  <div className="grid gap-2">
                    {accounts.map((account) => (
                      <button
                        key={account._id}
                        type="button"
                        onClick={() => handleSelect(account)}
                        className="flex items-center justify-between p-4 hover:bg-blue-50 rounded-2xl transition-all border border-transparent hover:border-blue-100 text-left group"
                      >
                        <div className="min-w-0">
                          <div className="font-bold text-slate-800 group-hover:text-blue-700 truncate">
                            {account.name}
                          </div>
                          <div className="text-[10px] font-black text-slate-400 uppercase tracking-tighter">
                            {account.code || 'Kodsuz'}
                            {account.phone ? ` • ${account.phone}` : ''}
                            {account.phone2 ? ` • ${account.phone2}` : ''}
                            {account.taxNumber ? ` • VKN/TC: ${account.taxNumber}` : ''}
                            {account.city ? ` • ${account.city}` : ''}
                            {account.type ? ` • ${getAccountTypeLabel(account.type)}` : ''}
                          </div>
                        </div>
                        <div className="text-[10px] font-black bg-white px-3 py-1 rounded-lg border border-slate-100 text-slate-400 group-hover:text-blue-600 group-hover:border-blue-200">
                          SEÇ
                        </div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="rounded-[2rem] border border-dashed border-slate-200 bg-slate-50/70 p-8 text-center space-y-4">
                    <p className="text-xs font-bold text-slate-400 uppercase">{emptyStateLabel}</p>
                    <p className="text-[10px] font-medium text-slate-400">
                      {accountType === 'EMPLOYEE' ? 'Durum filtresini değiştirebilir veya yeni personel oluşturabilirsiniz.' : 'Aradığınız kayıt bulunamadıysa yeni müşteri ekleyebilirsiniz.'}
                    </p>
                    {canQuickCreate && (
                      <button
                        type="button"
                        onClick={openCreateView}
                        className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-4 py-2 text-[10px] font-black uppercase tracking-widest text-white transition-colors hover:bg-black"
                      >
                        <Plus size={12} />
                        + Yeni Müşteri Ekle
                      </button>
                    )}
                  </div>
                )}
              </div>

              <div className="border-t border-slate-50 bg-slate-50/30 px-6 py-4">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                    {summaryLabel || ' '}
                  </p>
                  {pagination.totalPages > 1 && (
                    <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                      {pagination.currentPage}/{pagination.totalPages}
                    </div>
                  )}
                </div>
                {pagination.totalPages > 1 && (
                  <div className="mt-3">
                    <Pagination
                      currentPage={pagination.currentPage}
                      totalPages={pagination.totalPages}
                      onPageChange={(newPage) => fetchAccounts(search.trim(), newPage)}
                    />
                  </div>
                )}
              </div>
            </section>

            <section className="flex h-full w-1/2 shrink-0 flex-col overflow-hidden">
              <form onSubmit={handleCreateSubmit} className="flex h-full flex-col">
                <div className="flex items-center justify-between gap-4 border-b border-slate-50 bg-slate-50/50 p-6">
                  <div className="min-w-0">
                    <p className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-400">Hızlı Oluşturma</p>
                    <h3 className="mt-1 truncate text-lg font-black tracking-tight text-slate-900">
                      Yeni Müşteri Ekle
                    </h3>
                  </div>
                  <button
                    type="button"
                    onClick={handleCreateBack}
                    className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-2 text-[10px] font-black uppercase tracking-widest text-slate-500 transition-colors hover:text-slate-900"
                  >
                    <ChevronLeft size={14} />
                    Geri
                  </button>
                </div>

                <div className="flex-1 space-y-5 overflow-y-auto p-6">
                  <div className="grid gap-4 md:grid-cols-2">
                    <label className="flex flex-col gap-1">
                      <span className="mb-1 ml-1 block text-[10px] font-black uppercase tracking-widest text-slate-400">
                        Kod
                      </span>
                      <input
                        required
                        value={createForm.code}
                        onChange={handleCreateFieldChange('code')}
                        className={`w-full rounded-2xl border px-4 py-3 text-sm font-bold outline-none transition-all ${
                          createCodeChecking
                            ? 'border-blue-300 bg-blue-50'
                            : isCreateCodeAvailable
                              ? 'border-slate-200 bg-slate-50 focus:border-blue-500 focus:bg-white'
                              : 'border-rose-500 bg-rose-50 text-rose-700 focus:border-rose-500 focus:bg-rose-50'
                        }`}
                        placeholder="Kod"
                      />
                      <p className={`mt-1 text-[10px] font-black uppercase tracking-widest ${isCreateCodeAvailable ? 'text-slate-400' : 'text-rose-500'}`}>
                        {createCodeChecking
                          ? 'Kod doğrulanıyor...'
                          : isCreateCodeAvailable
                            ? 'Kod hazır'
                            : 'Bu cari kodu kullanımda'}
                      </p>
                      <button
                        type="button"
                        onClick={handleCreateCodeRefresh}
                        className="mt-2 inline-flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-slate-400 transition-colors hover:text-slate-700"
                      >
                        {createCodeChecking && <Loader2 size={10} className="animate-spin" />}
                        Kodu Yenile
                      </button>
                    </label>

                    <label className="flex flex-col gap-1">
                      <span className="mb-1 ml-1 block text-[10px] font-black uppercase tracking-widest text-slate-400">
                        Tür
                      </span>
                      <select
                        value={accountType}
                        disabled
                        className="w-full rounded-2xl border border-slate-200 bg-slate-100 px-4 py-3 text-sm font-bold text-slate-500 outline-none"
                      >
                        <option value={accountType}>{getAccountTypeLabel(accountType)}</option>
                      </select>
                    </label>

                    <label className="md:col-span-2 flex flex-col gap-1 pt-2 border-t border-slate-50">
                      <span className="mb-1 ml-1 block text-[10px] font-black uppercase tracking-widest text-slate-400">
                        Cari Ünvanı *
                      </span>
                      <input
                        required
                        value={createForm.name}
                        onChange={handleCreateFieldChange('name')}
                        className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-bold outline-none transition-all focus:border-blue-500 focus:bg-white"
                        placeholder="Yeni müşteri unvanı"
                      />
                    </label>

                    <label className="flex flex-col gap-1">
                      <span className="mb-1 ml-1 block text-[10px] font-black uppercase tracking-widest text-slate-400">
                        Vergi Dairesi
                      </span>
                      <input
                        value={createForm.taxOffice}
                        onChange={handleCreateFieldChange('taxOffice')}
                        className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-bold outline-none transition-all focus:border-blue-500 focus:bg-white"
                        placeholder="Vergi dairesi"
                      />
                    </label>

                    <label className="flex flex-col gap-1">
                      <span className="mb-1 ml-1 block text-[10px] font-black uppercase tracking-widest text-slate-400">
                        Vergi / TC No
                      </span>
                      <input
                        value={createForm.taxNumber}
                        onChange={handleCreateFieldChange('taxNumber')}
                        className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-bold outline-none transition-all focus:border-blue-500 focus:bg-white"
                        placeholder="Vergi / TC No"
                      />
                    </label>

                    <label className="flex flex-col gap-1">
                      <span className="mb-1 ml-1 block text-[10px] font-black uppercase tracking-widest text-slate-400">
                        Telefon
                      </span>
                      <input
                        type="tel"
                        value={createForm.phone}
                        onChange={handleCreateFieldChange('phone')}
                        className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-bold outline-none transition-all focus:border-blue-500 focus:bg-white"
                        placeholder="+90 5xx xxx xx xx"
                      />
                    </label>

                    <label className="flex flex-col gap-1">
                      <span className="mb-1 ml-1 block text-[10px] font-black uppercase tracking-widest text-slate-400">
                        Telefon 2
                      </span>
                      <input
                        type="tel"
                        value={createForm.phone2}
                        onChange={handleCreateFieldChange('phone2')}
                        className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-bold outline-none transition-all focus:border-blue-500 focus:bg-white"
                        placeholder="+90 5xx xxx xx xx"
                      />
                    </label>

                    <label className="md:col-span-2 flex flex-col gap-1">
                      <span className="mb-1 ml-1 block text-[10px] font-black uppercase tracking-widest text-slate-400">
                        E-posta
                      </span>
                      <input
                        type="email"
                        value={createForm.email}
                        onChange={handleCreateFieldChange('email')}
                        className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-bold outline-none transition-all focus:border-blue-500 focus:bg-white"
                        placeholder="ornek@firma.com"
                      />
                    </label>
                  </div>
                </div>

                <div className="mt-auto border-t border-slate-50 bg-slate-50/30 p-6">
                  <div className="flex flex-col gap-3 sm:flex-row">
                    <button
                      type="button"
                      onClick={handleCreateBack}
                      className="rounded-2xl bg-slate-100 px-5 py-3 text-xs font-black uppercase tracking-widest text-slate-600 transition-colors hover:bg-slate-200 sm:flex-1"
                    >
                      İptal
                    </button>
                    <button
                      type="submit"
                      disabled={createSaving || !isCreateCodeAvailable}
                      className="inline-flex items-center justify-center gap-2 rounded-2xl bg-blue-600 px-5 py-3 text-xs font-black uppercase tracking-widest text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60 sm:flex-1"
                    >
                      {createSaving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                      Kaydet
                    </button>
                  </div>
                </div>
              </form>
            </section>
          </div>
        </div>

      </div>
      {quickPersonnelOpen && <QuickPersonnelModal onClose={() => setQuickPersonnelOpen(false)} onCreated={(personnel) => {
        setQuickPersonnelOpen(false);
        setAccounts((current) => [personnel, ...current]);
        handleSelect(personnel);
      }} />}
    </div>
  );
};

export default AccountSelectModal;
